import {
  animate, state,
  style,
  transition, trigger
} from '@angular/animations';
import {
  ChangeDetectorRef, Component,
  Input, OnDestroy, OnInit
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MxMenuService } from '../../services';
import { MainComponent } from './main.component';

@Component({ standalone: false,
  /* tslint:disable:component-selector */
  selector: '[mx-menuitem]',
  /* tslint:enable:component-selector */
  template: `
    <ng-container>
      <div
        *ngIf="root && item.visible !== false"
        class="layout-menuitem-root-text"
      >
        {{ ('page:' + item.label)  | i18next }}
      </div>
      <a
        [attr.href]="item.url"
        (click)="itemClick($event)"
        *ngIf="(!item.routerLink || item.items) && item.visible !== false"
        (mouseenter)="onMouseEnter()"
        (keydown.enter)="itemClick($event)"
        [attr.target]="item.target"
        [attr.tabindex]="0"
        [ngClass]="item.class"
        pRipple
      >
        <i [ngClass]="item.icon" class="layout-menuitem-icon"></i>
        <span class="layout-menuitem-text">{{ ('page:' + item.label)  | i18next }}</span>
        <i
          class="fa fa-angle-down fa-fw layout-submenu-toggler"
          *ngIf="item.items"
        ></i>
        <span class="menuitem-badge" *ngIf="item.badge">{{ item.badge }}</span>
      </a>
      <a
        (click)="itemClick($event)"
        (mouseenter)="hover = true"
        (mouseleave)="hover = false"
        *ngIf="item.routerLink && !item.items && item.visible !== false"
        [routerLink]="item.routerLink"
        routerLinkActive="active-menuitem-routerlink"
        [routerLinkActiveOptions]="{ exact: true }"
        [attr.target]="item.target"
        [attr.tabindex]="0"
        [ngClass]="item.class"
        pRipple
      >
        <i [ngClass]="item.icon" class="layout-menuitem-icon"></i>
        <span class="layout-menuitem-text">{{ ('page:' + item.label) | i18next }}</span>
        <i
          class="fa fa-angle-down fa-fw layout-submenu-toggler"
          *ngIf="item.items"
        ></i>
        <span class="menuitem-badge" *ngIf="item.badge">{{ item.badge }}</span>
      </a>
      <ul
        *ngIf="
          ((item.items && active) ||
            (item.items && main.isStatic() && (root || active))) &&
          item.visible !== false
        "
        [@children]="
          (main.isSlim() || main.isHorizontal()) && !main.isMobile() && root
            ? active
              ? 'visible'
              : 'hidden'
            : main.isStatic() && root && !main.isMobile()
            ? 'visible'
            : active
            ? 'visibleAnimated'
            : 'hiddenAnimated'
        "
      >
        <ng-template ngFor let-child let-i="index" [ngForOf]="item.items">
          <li
            mx-menuitem
            [item]="child"
            [index]="i"
            [parentKey]="key"
            [class]="child.badgeClass"
          ></li>
        </ng-template>
      </ul>
    </ng-container>
  `,
  host: {
    '[class.layout-root-menuitem]': 'root',
    '[class.active-menuitem]': 'active',
  },
  animations: [
    trigger('children', [
      state(
        'void',
        style({
          height: '0px',
        })
      ),
      state(
        'hiddenAnimated',
        style({
          height: '0px',
        })
      ),
      state(
        'visibleAnimated',
        style({
          height: '*',
        })
      ),
      state(
        'visible',
        style({
          height: '*',
          'z-index': 100,
        })
      ),
      state(
        'hidden',
        style({
          height: '0px',
          'z-index': '*',
        })
      ),
      transition(
        'visibleAnimated => hiddenAnimated',
        animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')
      ),
      transition(
        'hiddenAnimated => visibleAnimated',
        animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')
      ),
      transition(
        'void => visibleAnimated, visibleAnimated => void',
        animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')
      ),
    ]),
  ],
})
export class MenuitemComponent implements OnInit, OnDestroy {
  @Input() item: any;

  @Input() index: number;

  @Input() root: boolean;

  @Input() parentKey: string;

  active = false;

  hover: boolean;

  menuSourceSubscription: Subscription;

  menuResetSubscription: Subscription;

  key: string;

  constructor(
    public main: MainComponent,
    public router: Router,
    private cd: ChangeDetectorRef,
    private menuService: MxMenuService
  ) {
    this.menuSourceSubscription = this.menuService.menuSource$.subscribe(
      (key) => {
        // deactivate current active menu
        if (this.active && this.key !== key && key.indexOf(this.key) !== 0) {
          this.active = false;
        }
      }
    );

    this.menuResetSubscription = this.menuService.resetSource$.subscribe(() => {
      this.active = false;
    });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((params) => {
        if (
          (this.main.isSlim() || this.main.isHorizontal()) &&
          !this.main.isMobile()
        ) {
          this.active = false;
        } else {
          if (this.item.routerLink) {
            this.updateActiveStateFromRoute();
          } else {
            this.active = false;
          }
        }
      });
  }

  ngOnInit() {
    if (
      !(this.main.isSlim() || this.main.isHorizontal()) &&
      this.item.routerLink
    ) {
      this.updateActiveStateFromRoute();
    }

    this.key = this.parentKey
      ? this.parentKey + '-' + this.index
      : String(this.index);
  }

  updateActiveStateFromRoute() {
    this.active = this.router.isActive(
      this.item.routerLink[0],
      this.item.items ? false : true
    );
  }

  itemClick(event: Event) {
    // avoid processing disabled items
    if (this.item.disabled) {
      event.preventDefault();
      return;
    }

    // navigate with hover in horizontal mode
    if (this.root) {
      this.main.menuHoverActive = !this.main.menuHoverActive;
    }

    // notify other items
    this.menuService.onMenuStateChange(this.key);

    // execute command
    if (this.item.command) {
      this.item.command({ originalEvent: event, item: this.item });
    }

    // toggle active state
    if (this.item.items) {
      this.active = !this.active;
    } else {
      // activate item
      this.active = true;

      if (this.main.isMobile()) {
        this.main.staticMenuMobileActive = false;
      }

      if (this.main.isOverlay()) {
        this.main.overlayMenuActive = false;
      }

      // reset horizontal menu
      if (
        (this.main.isSlim() || this.main.isHorizontal()) &&
        !this.main.isMobile()
      ) {
        this.menuService.reset();
        this.main.menuHoverActive = false;
      }
    }
  }

  onMouseEnter() {
    // activate item on hover
    if (
      this.root &&
      this.main.menuHoverActive &&
      (this.main.isSlim() || this.main.isHorizontal()) &&
      this.main.isDesktop()
    ) {
      this.menuService.onMenuStateChange(this.key);
      this.active = true;
    }
  }

  ngOnDestroy() {
    if (this.menuSourceSubscription) {
      this.menuSourceSubscription.unsubscribe();
    }

    if (this.menuResetSubscription) {
      this.menuResetSubscription.unsubscribe();
    }
  }
}
